/**
 * Mock data for the Operations dashboard.
 *
 * Synthetic daily order and monthly finance aggregates. The dashboard
 * labels both consumers as Demo data; the Communication Gantt is live.
 */

export type OrderStatus = 'created' | 'in_progress' | 'delivered' | 'cancelled' | 'failed'

export interface DailyOrders {
  date: string // YYYY-MM-DD
  counts: Record<OrderStatus, number>
}

export interface MonthlyFinance {
  date: string // YYYY-MM-DD (first day of "bucket")
  revenue: number
  expenses: number
  profit: number
}

const MONTH_DAYS = 30

function dayISO(offsetDays: number): string {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  return d.toISOString().slice(0, 10)
}

/**
 * Synthetic orders timeline — 30 days back, weekday-weighted with a slow
 * upward drift so the stacked chart looks like a real ops graph.
 */
export const dailyOrders: DailyOrders[] = Array.from({ length: MONTH_DAYS }, (_, i) => {
  const offset = -(MONTH_DAYS - 1 - i)
  const date = dayISO(offset)
  const dow = new Date(date).getDay() // 0=Sun
  const weekend = dow === 0 || dow === 6
  const drift = i / MONTH_DAYS // 0..1
  const base = 90 + Math.round(40 * drift)
  const seasonal = weekend ? 0.55 : 1.0
  const noise = (n: number) => Math.round(n * (0.9 + Math.random() * 0.2))
  const created = noise(base * seasonal)
  const inProgress = noise(created * 0.65)
  const delivered = noise(created * 0.78)
  const cancelled = noise(created * 0.05)
  const failed = noise(created * 0.03)
  return {
    date,
    counts: { created, in_progress: inProgress, delivered, cancelled, failed },
  }
})

/** Six months of finance, monthly buckets. */
export const monthlyFinance: MonthlyFinance[] = Array.from({ length: 6 }, (_, i) => {
  const d = new Date()
  d.setMonth(d.getMonth() - (5 - i))
  d.setDate(1)
  const drift = i / 5
  const baseRev = 220_000 + Math.round(180_000 * drift)
  const revenue = baseRev + Math.round(Math.random() * 25_000)
  const expenses = Math.round(revenue * (0.62 - drift * 0.05))
  const profit = revenue - expenses
  return { date: d.toISOString().slice(0, 10), revenue, expenses, profit }
})

export const ORDER_STATUS_COLOR: Record<OrderStatus, string> = {
  created: '#3b82f6', // blue
  in_progress: '#f59e0b', // amber
  delivered: '#10b981', // emerald
  cancelled: '#94a3b8', // slate
  failed: '#ef4444', // rose
}

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  created: 'Created',
  in_progress: 'In progress',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  failed: 'Failed',
}
